import { useCallback, useEffect, useId, useMemo, useState, type CSSProperties } from "react";
import { LogOut } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import { DialogFooter, DialogHeader, ErrorLine } from "~/components/CreateRoomDialog";
import { DangerButton, GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { UserAvatar } from "~/components/UserSearch";
import { useMatrix } from "~/context/MatrixContext";
import { getMembers, setMemberPowerLevel, type RoomMember, type RoomSummary } from "~/lib/matrix";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

interface LeaveRoomDialogProps {
  room: RoomSummary;
  onClose: () => void;
  initialMode?: "confirm" | "promote";
}

const ADMIN_POWER_LEVEL = 100;

export function LeaveRoomDialog({ room, onClose, initialMode = "confirm" }: LeaveRoomDialogProps) {
  const { client, userId, leaveRoom } = useMatrix();
  const titleId = `${useId()}-title`;
  const bodyId = `${useId()}-body`;
  const [mode, setMode] = useState<"confirm" | "promote">(initialMode);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchUnderline = useUnderline();
  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  const members = useMemo<RoomMember[]>(
    () => (client ? getMembers(client, room.id) : []),
    [client, room.id],
  );
  const me = members.find((member) => member.userId === userId) ?? null;
  const otherMembers = members.filter(
    (member) => member.userId !== userId && member.membership !== "invite",
  );
  const otherAdmins = otherMembers.filter((member) => member.powerLevel >= ADMIN_POWER_LEVEL);
  const needsAdminHandoff =
    !room.isDirect && !!me && me.powerLevel >= ADMIN_POWER_LEVEL && otherAdmins.length === 0;
  const filteredMembers = otherMembers.filter((member) => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return true;
    return (
      member.displayName.toLowerCase().includes(needle) ||
      member.userId.toLowerCase().includes(needle)
    );
  });
  const selectedUserVisible = filteredMembers.some((member) => member.userId === selectedUserId);

  useEffect(() => {
    if (mode !== "promote" || selectedUserId) return;
    setSelectedUserId(otherMembers[0]?.userId ?? null);
  }, [mode, otherMembers, selectedUserId]);

  const runLeave = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await leaveRoom(room.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't leave. Try again.");
    } finally {
      setBusy(false);
    }
  }, [leaveRoom, onClose, room.id]);

  const handleConfirm = useCallback(() => {
    if (needsAdminHandoff) {
      setMode("promote");
      setSelectedUserId(otherMembers[0]?.userId ?? null);
      return;
    }
    void runLeave();
  }, [needsAdminHandoff, otherMembers, runLeave]);

  const handlePromoteAndLeave = useCallback(async () => {
    if (!client || !selectedUserId || !selectedUserVisible) return;
    setBusy(true);
    setError(null);
    try {
      await setMemberPowerLevel(client, room.id, selectedUserId, ADMIN_POWER_LEVEL);
      await leaveRoom(room.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't leave. Try again.");
    } finally {
      setBusy(false);
    }
  }, [client, leaveRoom, onClose, room.id, selectedUserId, selectedUserVisible]);

  const actionName = room.isDirect ? "Close" : "Leave";
  const title = room.isDirect ? `Close ${room.name}?` : `Leave #${room.name}?`;
  const body = room.isDirect
    ? "This leaves the direct conversation and removes it from your sidebar. You can return if someone invites you again."
    : "You'll stop getting messages from this channel. You can rejoin if someone invites you.";

  return (
    <Dialog
      labelledBy={titleId}
      describedBy={bodyId}
      onClose={requestClose}
      width={mode === "promote" ? "440px" : "400px"}
      panelStyle={{ minHeight: mode === "confirm" ? 180 : undefined }}
    >
      <DialogHeader
        title={mode === "confirm" ? title : "Choose an admin"}
        titleId={titleId}
        onClose={requestClose}
      />
      <div
        style={{
          padding: "var(--chat-dialog-body-padding)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-3)",
        }}
      >
        {mode === "confirm" ? (
          <p id={bodyId} style={bodyStyle}>
            {body}
          </p>
        ) : (
          <>
            <p id={bodyId} style={bodyStyle}>
              You're the last admin. Make someone else an admin before leaving.
            </p>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                if (!selectedUserVisible || busy) return;
                event.preventDefault();
                void handlePromoteAndLeave();
              }}
              onFocus={searchUnderline.onFocus}
              onBlur={searchUnderline.onBlur}
              placeholder="Search members"
              aria-label="Search members"
              style={searchInputStyle(searchUnderline.borderColor)}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-1)" }}>
              {filteredMembers.map((member) => (
                <AdminPickerRow
                  key={member.userId}
                  member={member}
                  selected={member.userId === selectedUserId}
                  onSelect={() => setSelectedUserId(member.userId)}
                />
              ))}
              {filteredMembers.length === 0 && (
                <p style={{ ...bodyStyle, color: "var(--chat-fg-muted)" }}>
                  {otherMembers.length === 0
                    ? "No other members can become admin yet. Invite someone before leaving."
                    : "No members match."}
                </p>
              )}
            </div>
          </>
        )}
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <DialogFooter>
        <GhostButton onClick={requestClose} disabled={busy}>
          Cancel
        </GhostButton>
        {mode === "confirm" ? (
          <DangerButton onClick={handleConfirm} disabled={busy} autoFocus>
            {busy ? `${actionName}…` : actionName}
          </DangerButton>
        ) : (
          <PrimaryButton
            onClick={() => void handlePromoteAndLeave()}
            disabled={!selectedUserId || !selectedUserVisible || busy}
          >
            {busy ? "Leaving…" : "Make admin and leave"}
          </PrimaryButton>
        )}
      </DialogFooter>
    </Dialog>
  );
}

function AdminPickerRow({
  member,
  selected,
  onSelect,
}: {
  member: RoomMember;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className="chat-button"
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: "var(--chat-directory-row-height)",
        padding: "0 var(--chat-space-3)",
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: selected ? "var(--chat-surface-panel)" : "transparent",
        color: "var(--chat-fg-primary)",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <UserAvatar name={member.displayName} />
      <span style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}>
        <span style={memberNameStyle}>{member.displayName}</span>
        <span style={memberIdStyle}>{member.userId}</span>
      </span>
      {selected && (
        <LogOut size={16} strokeWidth={ICON_STROKE} style={{ color: "var(--chat-fg-secondary)" }} />
      )}
    </button>
  );
}

const bodyStyle: CSSProperties = {
  margin: 0,
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
};

function searchInputStyle(borderColor: string): CSSProperties {
  return {
    height: "var(--chat-directory-row-height)",
    width: "100%",
    border: "none",
    borderBottom: `1px solid ${borderColor}`,
    outline: "none",
    background: "transparent",
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-md-size)",
    transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
  };
}

const memberNameStyle: CSSProperties = {
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const memberIdStyle: CSSProperties = {
  color: "var(--chat-fg-muted)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
